<template>
  <v-card>
    <v-toolbar
      class="settings-window-toolbar"
      extension-height="40"
      flat
      height="80"
    >
      <v-avatar
        :color="$t('Settings.WorkingHour.Toolbar.Color')"
      >
        <v-icon dark>
          {{ $t('Settings.WorkingHour.Toolbar.Icon') }}
        </v-icon>
      </v-avatar>

      <v-toolbar-title class="pl-3">
        <div class="body-3">
          {{ $t('Settings.WorkingHour.Toolbar.Title') }}
        </div>

        <div class="text-caption">
          {{ $t('Settings.WorkingHour.Toolbar.Subtitle') }}
        </div>
      </v-toolbar-title>

      <v-spacer />

      <template #extension>
        <v-tabs
          v-model="tab"
          background-color="transparent"
          grow
          height="40"
          slider-color="blue"
        >
          <v-tab key="working-hours">
            {{ $t('Settings.WorkingHour.Tab.WorkingHours').toLocaleUpperCase($i18n.locale) }}
          </v-tab>

          <v-tab
            key="order-time-ranges"
          >
            {{ $t('Settings.WorkingHour.Tab.OrderingTimes').toLocaleUpperCase($i18n.locale) }}
          </v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <v-divider />

    <v-card-text
      id="settingsWindow"
      class="pa-3"
      style="overflow-y: auto;"
    >
      <template v-if="viewData">
        <v-tabs-items v-model="tab">
          <v-tab-item key="working-hours">
            <v-form
              ref="workingHoursForm"
              v-model="workingHoursFormValid"
              @submit.prevent
            >
              <v-container
                class="pa-0"
                fluid
              >
                <v-row dense>
                  <template v-if="!appConfig.LOCATION_DATA.CatalogOnly">
                    <v-col cols="12">
                      <v-subheader class="pl-0 d-inline-flex font-weight-medium">
                        {{ $t('Settings.WorkingHour.Field.WorkingHours.Title') }}
                      </v-subheader>

                      <v-divider />
                    </v-col>

                    <v-col>
                      <h5 class="text-body-2 font-weight-bold">
                        <lang-search-highlight
                          :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.OnlineOrderingStatus.Title')"
                          :search-text="langSearchText"
                          highlight
                        />
                      </h5>
                      <div class="text-caption">
                        <lang-search-highlight
                          :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.OnlineOrderingStatus.Subtitle')"
                          :search-text="langSearchText"
                          highlight
                        />
                      </div>
                    </v-col>
                    <v-col class="shrink">
                      <v-switch
                        v-model="onlineOrderingStatus"
                        :disabled="!userCanManage || (!viewData.settings.offer_delivery && !viewData.settings.offer_collection && !viewData.settings.offer_self_service)"
                        class="ma-1 d-inline-flex"
                        color="success"
                        hide-details
                        inset
                      >
                        <template #label>
                          <div

                            :class="[onlineOrderingStatus ? 'green--text' : 'red--text']"
                          >
                            <strong>{{
                              onlineOrderingStatus ? $t('Settings.WorkingHour.Field.WorkingHours.OnlineOrderingStatus.Open') : $t('Settings.WorkingHour.Field.WorkingHours.OnlineOrderingStatus.Closed')
                            }}</strong>
                          </div>
                        </template>
                      </v-switch>
                    </v-col>
                    <v-col cols="12">
                      <v-divider />
                    </v-col>

                    <v-col>
                      <h5 class="text-body-2 font-weight-bold">
                        <lang-search-highlight
                          :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.AlwaysOnlineOrdering.Title')"
                          :search-text="langSearchText"
                          highlight
                        />
                      </h5>

                      <div class="text-caption">
                        <lang-search-highlight
                          :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.AlwaysOnlineOrdering.Subtitle')"
                          :search-text="langSearchText"
                          highlight
                        />
                      </div>
                    </v-col>
                    <v-col class="shrink">
                      <v-switch
                        v-model="viewData.settings.online_ordering_always"
                        :disabled="!userCanManage"
                        class="ma-1 d-inline-flex"
                        color="success"
                        hide-details
                        inset
                      >
                        <template #label>
                          <div

                            :class="[viewData.settings.online_ordering_always ? 'green--text' : 'red--text']"
                          >
                            <strong>{{
                              viewData.settings.online_ordering_always ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')
                            }}</strong>
                          </div>
                        </template>
                      </v-switch>
                    </v-col>
                    <v-col cols="12">
                      <v-divider />
                    </v-col>
                  </template>

                  <v-col cols="12">
                    <v-expansion-panels
                      v-model="panel"
                      accordion
                    >
                      <!-- Store -->
                      <v-expansion-panel
                        :readonly="openingReadonly"
                        dark
                      >
                        <v-expansion-panel-header
                          :hide-actions="openingReadonly"
                          class="opening green lighten-1 pa-0"
                        >
                          <div
                            class="v-subheader green white--text"
                            style="width: 100%; height: 60px;"
                          >
                            <v-icon
                              class="pr-2"
                              dark
                            >
                              storefront
                            </v-icon>

                            <span :class="{'caption': $vuetify.breakpoint.xsOnly}">
                              <lang-search-highlight
                                :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.OpeningType.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>

                            <v-spacer />

                            <div :class="{'pr-4': openingReadonly}">
                              <v-btn-toggle
                                v-model="opening_type"
                                mandatory
                              >
                                <v-btn
                                  :disabled="!userCanManage"
                                  :style="opening_type==='24' ? 'min-width: 158px;' : 'min-width: 50px;'"
                                  text
                                  value="24"
                                >
                                  <svg
                                    height="24px"
                                    viewBox="0 0 612.001 612.001"
                                    width="24px"
                                    x="0px"
                                    xml:space="preserve"
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlns:xlink="http://www.w3.org/1999/xlink"
                                    y="0px"
                                  >
                                    <g>
                                      <path
                                        :fill="isDarkMode ? 'white' : 'black'"
                                        d="M201.582,398.487c0-57.43,74.473-66.617,74.473-98.255c0-13.901-10.759-22.212-24.221-22.212 c-18.613,0-28.715,18.851-28.715,18.851l-21.536-14.357c0,0,14.576-31.857,52.717-31.857c28.039,0,51.822,17.042,51.822,47.109 c0,51.147-71.331,59.896-72.007,89.962h74.473v24.897H202.934C202.258,407.475,201.582,402.981,201.582,398.487z M324.809,353.845 l68.646-100.503h34.322v94.219h20.642v24.021h-20.642v41.045h-28.495v-41.045h-74.473V353.845L324.809,353.845z M399.281,347.561 v-49.337c0-7.854,1.132-16.604,1.132-16.604h-0.457c0,0-3.58,8.512-7.854,14.576l-36.789,50.927v0.438H399.281L399.281,347.561z  M539.777,339.118l-5.543,19.855C502.727,471.835,387.76,543.053,274.033,514.826C164.799,487.716,96.911,381.87,113.235,274.033 l32.914,9.188c7.629,2.13,14.704-4.798,12.733-12.471L123.088,131.37c-1.97-7.672-11.506-10.334-17.166-4.792L3.097,227.26 c-5.66,5.542-3.198,15.134,4.433,17.264l35.826,10.001C16.62,400.396,106.611,545.678,253.463,583.919 c152.489,39.71,308.024-54.678,350.394-206.45l5.299-18.982c1.528-5.474-1.67-11.149-7.144-12.678l-49.557-13.835 C546.981,330.445,541.305,333.645,539.777,339.118z M598.336,252.268l-51.97,6.752c-5.636,0.732-9.61,5.894-8.879,11.53 l2.048,15.762c0.732,5.636,5.895,9.611,11.53,8.878l51.97-6.752c5.636-0.732,9.61-5.894,8.879-11.53l-2.049-15.762 C609.134,255.511,603.972,251.536,598.336,252.268z M558.328,154.064l-45.655,25.729c-4.951,2.79-6.703,9.065-3.913,14.017 l7.803,13.847c2.79,4.951,9.065,6.703,14.017,3.913l45.655-25.729c4.951-2.79,6.703-9.065,3.913-14.017l-7.803-13.847 C569.555,153.026,563.279,151.274,558.328,154.064z M484.447,77.998l-32.694,40.958c-3.546,4.441-2.818,10.917,1.623,14.461 l12.423,9.916c4.441,3.546,10.916,2.819,14.461-1.623l32.694-40.958c3.546-4.442,2.818-10.917-1.623-14.462l-12.423-9.916 C494.467,72.83,487.992,73.556,484.447,77.998z M387.45,35.147L372.48,85.37c-1.624,5.446,1.476,11.178,6.921,12.801l15.233,4.54 c5.445,1.624,11.177-1.476,12.801-6.921l14.97-50.223c1.623-5.446-1.476-11.177-6.922-12.801l-15.232-4.54 C394.805,26.602,389.073,29.701,387.45,35.147z M281.464,31.752l4.933,52.174c0.535,5.658,5.556,9.811,11.213,9.276l15.824-1.497 c5.658-0.535,9.811-5.555,9.276-11.213l-4.934-52.174c-0.535-5.658-5.556-9.81-11.213-9.275l-15.824,1.496 C285.082,21.074,280.929,26.094,281.464,31.752z M181.924,68.308l24.119,46.526c2.615,5.045,8.826,7.015,13.872,4.399l14.111-7.314 c5.045-2.616,7.015-8.826,4.399-13.872l-24.119-46.526c-2.615-5.045-8.826-7.015-13.872-4.399l-14.111,7.315 C181.278,57.051,179.309,63.262,181.924,68.308z"
                                      />
                                    </g>
                                  </svg>
                                  <span
                                    v-show="opening_type==='24' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{ $t('Settings.WorkingHour.Field.WorkingHours.OpeningType.AlwaysOpen') }}
                                  </span>
                                </v-btn>
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 58px;"
                                  text
                                  value="7"
                                >
                                  <v-icon>timer</v-icon>
                                  <span
                                    v-show="opening_type==='7' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{ $t('Settings.WorkingHour.Field.WorkingHours.OpeningType.FlexibleHours') }}
                                  </span>
                                </v-btn>
                              </v-btn-toggle>
                            </div>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <v-card flat>
                            <v-card-text>
                              <v-container
                                class="pa-0"
                                fluid
                              >
                                <v-row
                                  v-if="opening_type==='7'"
                                  class="align-center"
                                  dense
                                >
                                  <template v-for="(o, i) in viewData.opening">
                                    <v-col
                                      :key="'vflex_'+i"
                                      :class="[o.status ? 'green' : 'red', isDarkMode ? 'darken-4' : 'lighten-4']"
                                      cols="12"
                                    >
                                      <v-subheader
                                        class="pl-1 text-h6 d-inline-flex"
                                        style="height: 36px !important;"
                                      >
                                        {{ o.weekday_name }}
                                      </v-subheader>

                                      <v-switch
                                        v-model="o.status"
                                        :disabled="!userCanManage"
                                        :false-value="0"
                                        :label="o.status===1 ? $t('Common.OnlineStatus.Title.Open') : $t('Common.OnlineStatus.Title.Closed')"
                                        :true-value="1"
                                        class="mr-1 mt-1 float-right"
                                        color="success"
                                        hide-details
                                        inset
                                        @change="validateData"
                                      />
                                    </v-col>
                                    <template v-if="o.status">
                                      <v-col
                                        :key="o.type + o.weekday + 'opening_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'opening_time'"
                                          v-model="o.visible_opening_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.opening_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.opening_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                              :rules="[
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.Required'),
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.RequiredClose'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_opening_time"
                                            v-model="o.opening_time"
                                            :allowed-hours="(v) => allowedStartHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_opening_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'closing_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'closing_time'"
                                          v-model="o.visible_closing_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.closing_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.closing_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                              :rules="[
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.Required'),
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.RequiredOpen'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_closing_time"
                                            v-model="o.closing_time"
                                            :allowed-hours="(v) => allowedEndHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_closing_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'toggle_after'"
                                        cols="12"
                                      >
                                        <v-btn
                                          :color="o.after_hours_toggle ? 'blue' : 'grey'"
                                          dark
                                          @click="o.after_hours_toggle = !o.after_hours_toggle"
                                        >
                                          <v-icon left>
                                            {{ o.after_hours_toggle ? 'visibility_off' : 'visibility' }}
                                          </v-icon>
                                          {{
                                            $t('Settings.WorkingHour.Field.WorkingHours.ExtendedHour').toLocaleUpperCase($i18n.locale)
                                          }}
                                        </v-btn>
                                      </v-col>
                                      <template v-if="o.after_hours_toggle">
                                        <v-col
                                          :key="o.type + o.weekday + 'opening_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'opening_time_after'"
                                            v-model="o.visible_opening_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.opening_time_after"
                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.opening_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_opening_time_after"
                                              v-model="o.opening_time_after"
                                              :allowed-hours="(v) => allowedStartHours(v, o.opening_time_after, o.closing_tim_aftere)"
                                              :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_opening_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                        <v-col
                                          :key="o.type + o.weekday + 'closing_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'closing_time_after'"
                                            v-model="o.visible_closing_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.closing_time_after"
                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.closing_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_closing_time_after"
                                              v-model="o.closing_time_after"
                                              :allowed-hours="(v) => allowedEndHours(v, o.opening_time_after, o.closing_time_after)"
                                              :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_closing_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                      </template>
                                    </template>
                                    <v-col
                                      :key="'divider_'+i"
                                      cols="12"
                                    >
                                      <v-divider />
                                    </v-col>
                                  </template>
                                </v-row>
                              </v-container>
                            </v-card-text>
                          </v-card>
                        </v-expansion-panel-content>
                      </v-expansion-panel>

                      <!-- Delivery -->
                      <v-expansion-panel
                        v-if="viewData.delivery"
                        :disabled="!viewData.settings.offer_delivery"
                        :readonly="deliveryReadonly"
                        dark
                      >
                        <v-expansion-panel-header
                          :hide-actions="deliveryReadonly"
                          class="delivery purple lighten-1 pa-0 mt-6"
                        >
                          <div
                            class="v-subheader purple white--text"
                            style="width: 100%; height: 60px;"
                          >
                            <v-icon
                              class="pr-2"
                              dark
                            >
                              {{ $t('Settings.WorkingHour.Field.WorkingHours.DeliveryHour.Icon') }}
                            </v-icon>
                            <span :class="{'caption': $vuetify.breakpoint.xsOnly}">
                              <lang-search-highlight
                                :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.DeliveryHour.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                            <v-spacer />
                            <div :class="{'pr-4': deliveryReadonly}">
                              <v-btn-toggle
                                v-model="delivery_type"
                                mandatory
                              >
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 50px;"
                                  text
                                  value="same"
                                >
                                  <v-icon>{{ $t('Settings.WorkingHour.Field.WorkingHours.Button.Store.Icon') }}</v-icon>
                                  <span
                                    v-show="delivery_type==='same' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.Store.Title').toLocaleUpperCase($i18n.locale)
                                    }}
                                  </span>
                                </v-btn>
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 58px;"
                                  text
                                  value="7"
                                >
                                  <v-icon>
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.FlexibleHours.Icon')
                                    }}
                                  </v-icon>
                                  <span
                                    v-show="delivery_type==='7' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.FlexibleHours.Title').toLocaleUpperCase($i18n.locale)
                                    }}
                                  </span>
                                </v-btn>
                              </v-btn-toggle>
                            </div>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <v-card flat>
                            <v-card-text>
                              <v-container
                                class="pa-0"
                                fluid
                              >
                                <v-row
                                  v-if="delivery_type==='7'"
                                  class="align-center"
                                  dense
                                >
                                  <template
                                    v-for="(o, i) in viewData.delivery"
                                  >
                                    <v-col
                                      :key="'vflex_'+i"
                                      :class="[o.status ? 'green' : 'red', isDarkMode ? 'darken-4' : 'lighten-4']"
                                      cols="12"
                                    >
                                      <v-subheader
                                        class="pl-1 text-h6 d-inline-flex"
                                        style="height: 36px !important;"
                                      >
                                        {{ o.weekday_name }}
                                      </v-subheader>
                                      <v-switch
                                        v-model="o.status"
                                        :disabled="!userCanManage"
                                        :false-value="0"
                                        :label="o.status===1 ? $t('Common.OnlineStatus.Title.Open') : $t('Common.OnlineStatus.Title.Closed')"
                                        :true-value="1"
                                        class="mr-1 mt-1 float-right"
                                        color="success"
                                        hide-details
                                        inset
                                        @change="validateData"
                                      />
                                    </v-col>
                                    <template v-if="o.status">
                                      <v-col
                                        :key="o.type + o.weekday + 'opening_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'opening_time'"
                                          v-model="o.visible_opening_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.opening_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.opening_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                              :rules="[
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.Required'),
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.RequiredClose'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_opening_time"
                                            v-model="o.opening_time"
                                            :allowed-hours="(v) => allowedStartHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_opening_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'closing_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'closing_time'"
                                          v-model="o.visible_closing_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.closing_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.closing_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                              :rules="[
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.Required'),
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.RequiredOpen'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_closing_time"
                                            v-model="o.closing_time"
                                            :allowed-hours="(v) => allowedEndHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_closing_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'toggle_after'"

                                        cols="12"
                                      >
                                        <v-btn
                                          :color="o.after_hours_toggle ? 'blue' : 'grey'"
                                          dark
                                          @click="o.after_hours_toggle = !o.after_hours_toggle"
                                        >
                                          <v-icon left>
                                            {{ o.after_hours_toggle ? 'visibility_off' : 'visibility' }}
                                          </v-icon>
                                          {{
                                            $t('Settings.WorkingHour.Field.WorkingHours.ExtendedHour').toLocaleUpperCase($i18n.locale)
                                          }}
                                        </v-btn>
                                      </v-col>
                                      <template v-if="o.after_hours_toggle">
                                        <v-col
                                          :key="o.type + o.weekday + 'opening_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'opening_time_after'"
                                            v-model="o.visible_opening_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.opening_time_after"
                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.opening_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_opening_time_after"
                                              v-model="o.opening_time_after"
                                              :allowed-hours="(v) => allowedStartHours(v, o.opening_time_after, o.closing_tim_aftere)"
                                              :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_opening_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                        <v-col
                                          :key="o.type + o.weekday + 'closing_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'closing_time_after'"
                                            v-model="o.visible_closing_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.closing_time_after"
                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.closing_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_closing_time_after"
                                              v-model="o.closing_time_after"
                                              :allowed-hours="(v) => allowedEndHours(v, o.opening_time_after, o.closing_time_after)"
                                              :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_closing_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                      </template>
                                    </template>
                                    <v-col
                                      :key="'divider_'+i"
                                      cols="12"
                                    >
                                      <v-divider />
                                    </v-col>
                                  </template>
                                </v-row>
                              </v-container>
                            </v-card-text>
                          </v-card>
                        </v-expansion-panel-content>

                        <v-container fluid>
                          <v-row dense>
                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <div>{{ $t('Settings.WorkingHour.Field.WorkingHours.DeliveryHour.DeliveryService') }}</div>
                              <div class="mt-2">
                                <v-switch
                                  v-model="viewData.settings.offer_delivery"
                                  :disabled="!userCanManage"
                                  class="pa-0 mt-0 d-inline-flex"
                                  color="purple lighten-1"
                                  hide-details
                                  inset
                                  style="min-width: 180px;"
                                  @change="(v) => {
                                    if(!v) {
                                      panel=null
                                      delivery_type='same'
                                    }
                                  }"
                                >
                                  <template #label>
                                    <div

                                      :class="[viewData.settings.offer_delivery ? 'purple--text' : 'red--text']"
                                      class="text-body-1 text-no-wrap"
                                    >
                                      <strong>{{
                                        viewData.settings.offer_delivery ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')
                                      }}</strong>
                                    </div>
                                  </template>

                                  <template #prepend>
                                    <div

                                      class="grey--text text--darken-1 text-body-1 text-no-wrap"
                                    >
                                      <span :class="{'caption': $vuetify.breakpoint.xsOnly}">WEB</span>
                                    </div>
                                  </template>
                                </v-switch>

                                <v-switch
                                  v-model="viewData.settings.offer_delivery_pos"
                                  :disabled="!userCanManage"
                                  class="ml-6 pa-0 mt-0 d-inline-flex"
                                  color="purple lighten-1"
                                  hide-details
                                  inset
                                  @change="(v) => {
                                    if(!v) {
                                      panel=null
                                      delivery_type='same'
                                    }
                                  }"
                                >
                                  <template #label>
                                    <div

                                      :class="[viewData.settings.offer_delivery_pos ? 'purple--text' : 'red--text']"
                                      class="text-body-1 text-no-wrap"
                                    >
                                      <strong>{{
                                        viewData.settings.offer_delivery_pos ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')
                                      }}</strong>
                                    </div>
                                  </template>

                                  <template #prepend>
                                    <div

                                      class="grey--text text--darken-1 text-body-1 text-no-wrap"
                                    >
                                      <span :class="{'caption': $vuetify.breakpoint.xsOnly}">POS</span>
                                    </div>
                                  </template>
                                </v-switch>
                              </div>
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-select
                                v-model="viewData.settings.delivery_time"
                                :items="viewData.settings.delivery_time_range.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.WorkingHours.DeliveryHour.DefaultDeliveryHour')"
                                :readonly="(!viewData.settings.offer_delivery && !viewData.settings.offer_delivery_pos) || !userCanManage"
                                :rules="[(v) => !!v || $t('Settings.WorkingHour.Field.Error.Required')]"
                                color="purple lighten-1"
                                hide-details
                                item-text="title"
                                item-value="id"
                                small-chips
                              />
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel>

                      <!-- Collection -->
                      <v-expansion-panel
                        v-if="viewData.collection"
                        :disabled="!viewData.settings.offer_collection"
                        :readonly="collectionReadonly"
                        dark
                      >
                        <v-expansion-panel-header
                          :hide-actions="collectionReadonly"
                          class="collection blue darken-1 pa-0 mt-6"
                        >
                          <div
                            class="v-subheader blue darken-3 white--text"
                            style="width: 100%; height: 60px;"
                          >
                            <v-icon
                              class="pr-2"
                              dark
                            >
                              {{ $t('Settings.WorkingHour.Field.WorkingHours.CollectionHour.Icon') }}
                            </v-icon>
                            <span :class="{'caption': $vuetify.breakpoint.xsOnly}">
                              <lang-search-highlight
                                :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.CollectionHour.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                            <v-spacer />
                            <div :class="{'pr-4': collectionReadonly}">
                              <v-btn-toggle
                                v-model="collection_type"
                                mandatory
                              >
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 50px;"
                                  text
                                  value="same"
                                >
                                  <v-icon>{{ $t('Settings.WorkingHour.Field.WorkingHours.Button.Store.Icon') }}</v-icon>
                                  <span
                                    v-show="collection_type==='same' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.Store.Title').toLocaleUpperCase($i18n.locale)
                                    }}
                                  </span>
                                </v-btn>
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 58px;"
                                  text
                                  value="7"
                                >
                                  <v-icon>
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.FlexibleHours.Icon')
                                    }}
                                  </v-icon>
                                  <span
                                    v-show="collection_type==='7' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.FlexibleHours.Title').toLocaleUpperCase($i18n.locale)
                                    }}
                                  </span>
                                </v-btn>
                              </v-btn-toggle>
                            </div>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <v-card flat>
                            <v-card-text>
                              <v-container
                                class="pa-0"
                                fluid
                              >
                                <v-row
                                  v-if="collection_type==='7'"
                                  class="align-center"
                                  dense
                                >
                                  <template
                                    v-for="(o, i) in viewData.collection"
                                  >
                                    <v-col
                                      :key="'vflex_'+i"
                                      :class="[o.status ? 'green' : 'red', isDarkMode ? 'darken-4' : 'lighten-4']"
                                      cols="12"
                                    >
                                      <v-subheader
                                        class="pl-1 text-h6 d-inline-flex"
                                        style="height: 36px !important;"
                                      >
                                        {{ o.weekday_name }}
                                      </v-subheader>
                                      <v-switch
                                        v-model="o.status"
                                        :disabled="!userCanManage"
                                        :false-value="0"
                                        :label="o.status===1 ? $t('Common.OnlineStatus.Title.Open') : $t('Common.OnlineStatus.Title.Closed')"
                                        :true-value="1"
                                        class="mr-1 mt-1 float-right"
                                        color="success"
                                        hide-details
                                        inset
                                        @change="validateData"
                                      />
                                    </v-col>
                                    <template v-if="o.status">
                                      <v-col
                                        :key="o.type + o.weekday + 'opening_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'opening_time'"
                                          v-model="o.visible_opening_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.opening_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.opening_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                              :rules="[
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.Required'),
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.RequiredClose'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_opening_time"
                                            v-model="o.opening_time"
                                            :allowed-hours="(v) => allowedStartHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_opening_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'closing_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'closing_time'"
                                          v-model="o.visible_closing_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.closing_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.closing_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                              :rules="[
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.Required'),
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.RequiredOpen'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_closing_time"
                                            v-model="o.closing_time"
                                            :allowed-hours="(v) => allowedEndHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_closing_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'toggle_after'"

                                        cols="12"
                                      >
                                        <v-btn
                                          :color="o.after_hours_toggle ? 'blue' : 'grey'"
                                          dark
                                          @click="o.after_hours_toggle = !o.after_hours_toggle"
                                        >
                                          <v-icon left>
                                            {{ o.after_hours_toggle ? 'visibility_off' : 'visibility' }}
                                          </v-icon>
                                          {{
                                            $t('Settings.WorkingHour.Field.WorkingHours.ExtendedHour').toLocaleUpperCase($i18n.locale)
                                          }}
                                        </v-btn>
                                      </v-col>
                                      <template v-if="o.after_hours_toggle">
                                        <v-col
                                          :key="o.type + o.weekday + 'opening_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'opening_time_after'"
                                            v-model="o.visible_opening_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.opening_time_after"
                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.opening_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_opening_time_after"
                                              v-model="o.opening_time_after"
                                              :allowed-hours="(v) => allowedStartHours(v, o.opening_time_after, o.closing_tim_aftere)"
                                              :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_opening_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                        <v-col
                                          :key="o.type + o.weekday + 'closing_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'closing_time_after'"
                                            v-model="o.visible_closing_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.closing_time_after"

                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.closing_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_closing_time_after"
                                              v-model="o.closing_time_after"
                                              :allowed-hours="(v) => allowedEndHours(v, o.opening_time_after, o.closing_time_after)"
                                              :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_closing_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                      </template>
                                    </template>
                                    <v-col
                                      :key="'divider_'+i"
                                      cols="12"
                                    >
                                      <v-divider />
                                    </v-col>
                                  </template>
                                </v-row>
                              </v-container>
                            </v-card-text>
                          </v-card>
                        </v-expansion-panel-content>

                        <v-container fluid>
                          <v-row dense>
                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <div>{{ $t('Settings.WorkingHour.Field.WorkingHours.CollectionHour.CollectionService') }}</div>
                              <div class="mt-2">
                                <v-switch
                                  v-model="viewData.settings.offer_collection"
                                  :disabled="!userCanManage"
                                  class="pa-0 mt-0 d-inline-flex"
                                  color="blue darken-"
                                  hide-details
                                  inset
                                  style="min-width: 180px;"
                                  @change="(v) => {
                                    if(!v) {
                                      panel=null
                                      collection_type='same'
                                    }
                                  }"
                                >
                                  <template #label>
                                    <div

                                      :class="[viewData.settings.offer_collection ? 'blue--text' : 'red--text']"
                                      class="text-body-1"
                                    >
                                      <strong>{{
                                        viewData.settings.offer_collection ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')
                                      }}</strong>
                                    </div>
                                  </template>
                                  <template #prepend>
                                    <div

                                      class="grey--text text--darken-1 text-body-1 text-no-wrap"
                                    >
                                      <span :class="{'caption': $vuetify.breakpoint.xsOnly}">WEB</span>
                                    </div>
                                  </template>
                                </v-switch>

                                <v-switch
                                  v-model="viewData.settings.offer_collection_pos"
                                  :disabled="!userCanManage"
                                  class="pa-0 ml-6 mt-0 d-inline-flex"
                                  color="blue darken-"
                                  hide-details
                                  inset
                                  @change="(v) => {
                                    if(!v) {
                                      panel=null
                                      collection_type='same'
                                    }
                                  }"
                                >
                                  <template #label>
                                    <div

                                      :class="[viewData.settings.offer_collection_pos ? 'blue--text' : 'red--text']"
                                      class="text-body-1"
                                    >
                                      <strong>{{
                                        viewData.settings.offer_collection_pos ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')
                                      }}</strong>
                                    </div>
                                  </template>
                                  <template #prepend>
                                    <div

                                      class="grey--text text--darken-1 text-body-1 text-no-wrap"
                                    >
                                      <span :class="{'caption': $vuetify.breakpoint.xsOnly}">POS</span>
                                    </div>
                                  </template>
                                </v-switch>
                              </div>
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-select
                                v-model="viewData.settings.collection_time"
                                :items="viewData.settings.collection_time_range.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.WorkingHours.CollectionHour.DefaultCollectionHour')"
                                :readonly="(!viewData.settings.offer_collection && !viewData.settings.offer_collection_pos) || !userCanManage"
                                :rules="[(v) => !!v || $t('Settings.WorkingHour.Field.Error.Required')]"
                                color="purple lighten-1"
                                hide-details
                                item-text="title"
                                item-value="id"
                                small-chips
                              />
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel>

                      <!-- Self-Service -->
                      <v-expansion-panel
                        v-if="viewData.self_service"
                        :disabled="!viewData.settings.offer_self_service && !viewData.settings.offer_self_service_pos"
                        :readonly="selfServiceReadonly"
                        dark
                      >
                        <v-expansion-panel-header
                          :hide-actions="selfServiceReadonly"
                          class="collection teal darken-1 pa-0 mt-6"
                        >
                          <div
                            class="v-subheader teal darken-3 white--text"
                            style="width: 100%; height: 60px;"
                          >
                            <v-icon
                              class="pr-2"
                              dark
                            >
                              {{ $t('Settings.WorkingHour.Field.WorkingHours.SelfServiceHour.Icon') }}
                            </v-icon>
                            <span :class="{'caption': $vuetify.breakpoint.xsOnly}">
                              <lang-search-highlight
                                :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.SelfServiceHour.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                            <v-spacer />
                            <div :class="{'pr-4': selfServiceReadonly}">
                              <v-btn-toggle
                                v-model="self_service_type"
                                mandatory
                              >
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 50px;"
                                  text
                                  value="same"
                                >
                                  <v-icon>{{ $t('Settings.WorkingHour.Field.WorkingHours.Button.Store.Icon') }}</v-icon>
                                  <span
                                    v-show="self_service_type==='same' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.Store.Title').toLocaleUpperCase($i18n.locale)
                                    }}
                                  </span>
                                </v-btn>
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 58px;"
                                  text
                                  value="7"
                                >
                                  <v-icon>
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.FlexibleHours.Icon')
                                    }}
                                  </v-icon>
                                  <span
                                    v-show="self_service_type==='7' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.FlexibleHours.Title').toLocaleUpperCase($i18n.locale)
                                    }}
                                  </span>
                                </v-btn>
                              </v-btn-toggle>
                            </div>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <v-card flat>
                            <v-card-text>
                              <v-container
                                class="pa-0"
                                fluid
                              >
                                <v-row
                                  v-if="self_service_type==='7'"
                                  class="align-center"
                                  dense
                                >
                                  <template
                                    v-for="(o, i) in viewData.self_service"
                                  >
                                    <v-col
                                      :key="'vflex_'+i"
                                      :class="[o.status ? 'green' : 'red', isDarkMode ? 'darken-4' : 'lighten-4']"
                                      cols="12"
                                    >
                                      <v-subheader
                                        class="pl-1 text-h6 d-inline-flex"
                                        style="height: 36px !important;"
                                      >
                                        {{ o.weekday_name }}
                                      </v-subheader>
                                      <v-switch
                                        v-model="o.status"
                                        :disabled="!userCanManage"
                                        :false-value="0"
                                        :label="o.status===1 ? $t('Common.OnlineStatus.Title.Open') : $t('Common.OnlineStatus.Title.Closed')"
                                        :true-value="1"
                                        class="mr-1 mt-1 float-right"
                                        color="success"
                                        hide-details
                                        inset
                                        @change="validateData"
                                      />
                                    </v-col>
                                    <template v-if="o.status">
                                      <v-col
                                        :key="o.type + o.weekday + 'opening_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'opening_time'"
                                          v-model="o.visible_opening_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.opening_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.opening_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                              :rules="[
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.Required'),
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.RequiredClose'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_opening_time"
                                            v-model="o.opening_time"
                                            :allowed-hours="(v) => allowedStartHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_opening_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'closing_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'closing_time'"
                                          v-model="o.visible_closing_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.closing_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.closing_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                              :rules="[
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.Required'),
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.RequiredOpen'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_closing_time"
                                            v-model="o.closing_time"
                                            :allowed-hours="(v) => allowedEndHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_closing_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'toggle_after'"

                                        cols="12"
                                      >
                                        <v-btn
                                          :color="o.after_hours_toggle ? 'blue' : 'grey'"
                                          dark
                                          @click="o.after_hours_toggle = !o.after_hours_toggle"
                                        >
                                          <v-icon left>
                                            {{ o.after_hours_toggle ? 'visibility_off' : 'visibility' }}
                                          </v-icon>
                                          {{
                                            $t('Settings.WorkingHour.Field.WorkingHours.ExtendedHour').toLocaleUpperCase($i18n.locale)
                                          }}
                                        </v-btn>
                                      </v-col>
                                      <template v-if="o.after_hours_toggle">
                                        <v-col
                                          :key="o.type + o.weekday + 'opening_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'opening_time_after'"
                                            v-model="o.visible_opening_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.opening_time_after"
                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.opening_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_opening_time_after"
                                              v-model="o.opening_time_after"
                                              :allowed-hours="(v) => allowedStartHours(v, o.opening_time_after, o.closing_tim_aftere)"
                                              :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_opening_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                        <v-col
                                          :key="o.type + o.weekday + 'closing_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'closing_time_after'"
                                            v-model="o.visible_closing_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.closing_time_after"

                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.closing_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_closing_time_after"
                                              v-model="o.closing_time_after"
                                              :allowed-hours="(v) => allowedEndHours(v, o.opening_time_after, o.closing_time_after)"
                                              :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_closing_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                      </template>
                                    </template>
                                    <v-col
                                      :key="'divider_'+i"
                                      cols="12"
                                    >
                                      <v-divider />
                                    </v-col>
                                  </template>
                                </v-row>
                              </v-container>
                            </v-card-text>
                          </v-card>
                        </v-expansion-panel-content>

                        <v-container fluid>
                          <v-row dense>
                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <div>{{ $t('Settings.WorkingHour.Field.WorkingHours.SelfServiceHour.SelfServiceService') }}</div>
                              <div class="mt-2">
                                <v-switch
                                  v-model="viewData.settings.offer_self_service"
                                  :disabled="!userCanManage"
                                  class="pa-0 mt-0 d-inline-flex"
                                  color="teal darken-"
                                  hide-details
                                  inset
                                  style="min-width: 180px;"
                                  @change="(v) => {
                                    if(!v) {
                                      panel=null
                                      self_service_type='same'
                                    }
                                  }"
                                >
                                  <template #label>
                                    <div

                                      :class="[viewData.settings.offer_self_service ? 'teal--text' : 'red--text']"
                                      class="text-body-1"
                                    >
                                      <strong>{{
                                        viewData.settings.offer_self_service ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')
                                      }}</strong>
                                    </div>
                                  </template>
                                  <template #prepend>
                                    <div

                                      class="grey--text text--darken-1 text-body-1 text-no-wrap"
                                    >
                                      <span :class="{'caption': $vuetify.breakpoint.xsOnly}">WEB</span>
                                    </div>
                                  </template>
                                </v-switch>

                                <v-switch
                                  v-model="viewData.settings.offer_self_service_pos"
                                  :disabled="!userCanManage"
                                  class="pa-0 ml-6 mt-0 d-inline-flex"
                                  color="teal darken-"
                                  hide-details
                                  inset
                                  @change="(v) => {
                                    if(!v) {
                                      panel=null
                                      self_service_type='same'
                                    }
                                  }"
                                >
                                  <template #label>
                                    <div

                                      :class="[viewData.settings.offer_self_service_pos ? 'teal--text' : 'red--text']"
                                      class="text-body-1"
                                    >
                                      <strong>{{
                                        viewData.settings.offer_self_service_pos ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')
                                      }}</strong>
                                    </div>
                                  </template>
                                  <template #prepend>
                                    <div

                                      class="grey--text text--darken-1 text-body-1 text-no-wrap"
                                    >
                                      <span :class="{'caption': $vuetify.breakpoint.xsOnly}">POS</span>
                                    </div>
                                  </template>
                                </v-switch>
                              </div>
                            </v-col>

                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-select
                                v-model="viewData.settings.self_service_time"
                                :items="viewData.settings.self_service_time_range.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.WorkingHours.SelfServiceHour.DefaultSelfServiceHour')"
                                :readonly="(!viewData.settings.offer_self_service && !viewData.settings.offer_self_service_pos) || !userCanManage"
                                :rules="[(v) => !!v || $t('Settings.WorkingHour.Field.Error.Required')]"
                                color="purple lighten-1"
                                hide-details
                                item-text="title"
                                item-value="id"
                                small-chips
                              />
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel>

                      <!-- Reservation -->
                      <v-expansion-panel
                        v-if="viewData.reservation && posUserHasComponentPermission('SystemSettingsReservations', 'MANAGE')"
                        :disabled="!viewData.settings.offer_reservation"
                        :readonly="reservationReadonly"
                        dark
                      >
                        <v-expansion-panel-header
                          :hide-actions="reservationReadonly"
                          class="reservation indigo darken-1 pa-0 mt-6"
                        >
                          <div
                            class="v-subheader indigo darken-3 white--text"
                            style="width: 100%; height: 60px;"
                          >
                            <v-icon
                              class="pr-2"
                              dark
                            >
                              {{ $t('Settings.WorkingHour.Field.WorkingHours.ReservationHour.Icon') }}
                            </v-icon>
                            <span :class="{'caption': $vuetify.breakpoint.xsOnly}">
                              <lang-search-highlight
                                :lang-text="$t('Settings.WorkingHour.Field.WorkingHours.ReservationHour.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                            <v-spacer />
                            <div :class="{'pr-4': reservationReadonly}">
                              <v-btn-toggle
                                v-model="reservation_type"
                                mandatory
                              >
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 50px;"
                                  text
                                  value="same"
                                >
                                  <v-icon>{{ $t('Settings.WorkingHour.Field.WorkingHours.Button.Store.Icon') }}</v-icon>
                                  <span
                                    v-show="reservation_type==='same' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.Store.Title').toLocaleUpperCase($i18n.locale)
                                    }}
                                  </span>
                                </v-btn>
                                <v-btn
                                  :disabled="!userCanManage"
                                  style="min-width: 58px;"
                                  text
                                  value="7"
                                >
                                  <v-icon>
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.FlexibleHours.Icon')
                                    }}
                                  </v-icon>
                                  <span
                                    v-show="reservation_type==='7' && $vuetify.breakpoint.smAndUp"
                                    class="pl-2"
                                  >
                                    {{
                                      $t('Settings.WorkingHour.Field.WorkingHours.Button.FlexibleHours.Title').toLocaleUpperCase($i18n.locale)
                                    }}
                                  </span>
                                </v-btn>
                              </v-btn-toggle>
                            </div>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <v-card flat>
                            <v-card-text>
                              <v-container
                                class="pa-0"
                                fluid
                              >
                                <v-row
                                  v-if="reservation_type==='7'"
                                  class="align-center"
                                  dense
                                >
                                  <template
                                    v-for="(o, i) in viewData.reservation"
                                  >
                                    <v-col
                                      :key="'vflex_'+i"
                                      :class="[o.status ? 'green' : 'red', isDarkMode ? 'darken-4' : 'lighten-4']"
                                      cols="12"
                                    >
                                      <v-subheader
                                        class="pl-1 text-h6 d-inline-flex"
                                        style="height: 36px !important;"
                                      >
                                        {{ o.weekday_name }}
                                      </v-subheader>
                                      <v-switch
                                        v-model="o.status"
                                        :disabled="!userCanManage"
                                        :false-value="0"
                                        :label="o.status===1 ? $t('Common.OnlineStatus.Title.Open') : $t('Common.OnlineStatus.Title.Closed')"
                                        :true-value="1"
                                        class="mr-1 mt-1 float-right"
                                        color="success"
                                        hide-details
                                        inset
                                        @change="validateData"
                                      />
                                    </v-col>
                                    <template v-if="o.status">
                                      <v-col
                                        :key="o.type + o.weekday + 'opening_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'opening_time'"
                                          v-model="o.visible_opening_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.opening_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.opening_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                              :rules="[
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.Required'),
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Error.RequiredClose'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_opening_time"
                                            v-model="o.opening_time"
                                            :allowed-hours="(v) => allowedStartHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_opening_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'opening_time'][0].save(o.opening_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'closing_time_1'"
                                        class="text-center"
                                        cols="6"
                                      >
                                        <v-menu
                                          :ref="o.type + o.weekday + 'closing_time'"
                                          v-model="o.visible_closing_time"
                                          :close-on-content-click="false"
                                          :disabled="!o.status || !userCanManage"
                                          :return-value.sync="o.closing_time"
                                          max-width="290px"
                                          min-width="290px"
                                          offset-y
                                          transition="scale-transition"
                                        >
                                          <template #activator="{on}">
                                            <v-text-field
                                              v-model="o.closing_time"
                                              :disabled="!o.status || !userCanManage"
                                              :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                              :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                              :rules="[
                                                () => !!o.closing_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.Required'),
                                                () => !!o.opening_time || $t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Error.RequiredOpen'),
                                              ]"
                                              filled
                                              hide-details
                                              readonly
                                              required
                                              v-on="on"
                                            />
                                          </template>

                                          <v-time-picker
                                            v-if="o.visible_closing_time"
                                            v-model="o.closing_time"
                                            :allowed-hours="(v) => allowedEndHours(v, o.opening_time, o.closing_time)"
                                            :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time, o.closing_time)"
                                            :disabled="!userCanManage"
                                            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                            first-day-of-week="1"
                                            format="24hr"
                                            scrollable
                                            @change="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                          >
                                            <v-spacer />
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="o.visible_closing_time = false"
                                            >
                                              {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                            <v-btn
                                              color="primary"
                                              text
                                              @click="$refs[o.type + o.weekday + 'closing_time'][0].save(o.closing_time)"
                                            >
                                              {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                            </v-btn>
                                          </v-time-picker>
                                        </v-menu>
                                      </v-col>
                                      <v-col
                                        :key="o.type + o.weekday + 'toggle_after'"

                                        cols="12"
                                      >
                                        <v-btn
                                          :color="o.after_hours_toggle ? 'blue' : 'grey'"
                                          dark
                                          @click="o.after_hours_toggle = !o.after_hours_toggle"
                                        >
                                          <v-icon left>
                                            {{ o.after_hours_toggle ? 'visibility_off' : 'visibility' }}
                                          </v-icon>
                                          {{
                                            $t('Settings.WorkingHour.Field.WorkingHours.ExtendedHour').toLocaleUpperCase($i18n.locale)
                                          }}
                                        </v-btn>
                                      </v-col>
                                      <template v-if="o.after_hours_toggle">
                                        <v-col
                                          :key="o.type + o.weekday + 'opening_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'opening_time_after'"
                                            v-model="o.visible_opening_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.opening_time_after"
                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.opening_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.OpeningTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_opening_time_after"
                                              v-model="o.opening_time_after"
                                              :allowed-hours="(v) => allowedStartHours(v, o.opening_time_after, o.closing_tim_aftere)"
                                              :allowed-minutes="(v) => allowedStartMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_opening_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'opening_time_after'][0].save(o.opening_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                        <v-col
                                          :key="o.type + o.weekday + 'closing_time_2'"
                                          class="text-center mt-0 pt-0"
                                          cols="6"
                                        >
                                          <v-menu
                                            :ref="o.type + o.weekday + 'closing_time_after'"
                                            v-model="o.visible_closing_time_after"
                                            :close-on-content-click="false"
                                            :disabled="!o.status || !userCanManage"
                                            :return-value.sync="o.closing_time_after"
                                            max-width="290px"
                                            min-width="290px"
                                            offset-y
                                            transition="scale-transition"
                                          >
                                            <template #activator="{on}">
                                              <v-text-field
                                                v-model="o.closing_time_after"
                                                :disabled="!o.status || !userCanManage"
                                                :error="o.error"
                                                :error-messages="o.error_messages ? o.error_messages : []"
                                                :label="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Label')"
                                                :prepend-inner-icon="$t('Settings.WorkingHour.Field.WorkingHours.ClosingTime.Icon')"
                                                clearable
                                                filled
                                                persistent-hint
                                                readonly
                                                required
                                                v-on="on"
                                              />
                                            </template>

                                            <v-time-picker
                                              v-if="o.visible_closing_time_after"
                                              v-model="o.closing_time_after"
                                              :allowed-hours="(v) => allowedEndHours(v, o.opening_time_after, o.closing_time_after)"
                                              :allowed-minutes="(v) => allowedEndMinutes(v, o.opening_time_after, o.closing_time_after)"
                                              :disabled="!userCanManage"
                                              :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                                              first-day-of-week="1"
                                              format="24hr"
                                              scrollable
                                              @change="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                            >
                                              <v-spacer />
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="o.visible_closing_time_after = false"
                                              >
                                                {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                              <v-btn
                                                color="primary"
                                                text
                                                @click="$refs[o.type + o.weekday + 'closing_time_after'][0].save(o.closing_time_after)"
                                              >
                                                {{ $t('Common.Button.Ok').toLocaleUpperCase($i18n.locale) }}
                                              </v-btn>
                                            </v-time-picker>
                                          </v-menu>
                                        </v-col>
                                      </template>
                                    </template>
                                    <v-col
                                      :key="'divider_'+i"
                                      cols="12"
                                    >
                                      <v-divider />
                                    </v-col>
                                  </template>
                                </v-row>
                              </v-container>
                            </v-card-text>
                          </v-card>
                        </v-expansion-panel-content>

                        <v-container fluid>
                          <v-row dense>
                            <v-col
                              cols="12"
                              sm="6"
                            >
                              <v-switch
                                v-model="viewData.settings.offer_reservation"
                                :disabled="!userCanManage"
                                class="pa-0 d-inline-flex"
                                color="indigo darken-"
                                hide-details
                                inset
                                @change="(v) => {
                                  if(!v) {
                                    panel=null
                                    reservation_type='same'
                                  }
                                }"
                              >
                                <template #label>
                                  <div

                                    :class="[viewData.settings.offer_reservation ? 'indigo--text' : 'red--text']"
                                    class="text-body-1"
                                  >
                                    <strong>{{
                                      viewData.settings.offer_reservation ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')
                                    }}</strong>
                                  </div>
                                </template>
                                <template #prepend>
                                  <div

                                    class="grey--text text--darken-1 text-body-1 text-no-wrap"
                                  >
                                    <span :class="{'caption': $vuetify.breakpoint.xsOnly}">{{
                                      $t('Settings.WorkingHour.Field.WorkingHours.ReservationHour.ReservationService')
                                    }}</span>
                                  </div>
                                </template>
                              </v-switch>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-tab-item>

          <v-tab-item
            key="order-time-ranges"
          >
            <v-form
              ref="orderTimeRangesForm"
              v-model="orderTimeRangesFormValid"
              @submit.prevent
            >
              <v-container
                class="pa-0"
                fluid
              >
                <v-row dense>
                  <v-col cols="12">
                    <v-subheader class="pl-0 d-inline-flex font-weight-medium">
                      {{ $t('Settings.WorkingHour.Field.OrderingTimes.Title') }}
                    </v-subheader>
                    <v-divider />
                  </v-col>

                  <v-col
                    v-if="userIsSuperAdmin"
                    cols="12"
                  >
                    <v-combobox
                      v-model="viewData.settings.order_time_ranges"
                      :label="$t('Settings.WorkingHour.Field.OrderingTimes.AddTime')"
                      :search-input.sync="AddNewTimeSearch"
                      color="green lighten-1"
                      deletable-chips
                      hide-details
                      hide-selected
                      item-text="title"
                      item-value="id"
                      multiple
                      small-chips
                      type="number"
                      @change="onOrderTimeRangeChange"
                    >
                      <template #no-data>
                        <v-list-item>
                          <span class="text-subtitle-1 mr-2">{{ $t('Common.Button.Add') }}: </span>

                          <v-chip
                            v-show="AddNewTimeSearch"
                            label
                            small
                          >
                            {{ convertMinutesToDisplayString(AddNewTimeSearch, false, false) }}
                          </v-chip>
                        </v-list-item>
                      </template>

                      <template #selection="{item, parent, selected}">
                        <v-chip
                          :value="selected"
                          label
                          small
                        >
                          <span class="pr-2">
                            {{ convertMinutesToDisplayString(item, false, false) }}
                          </span>

                          <v-icon
                            small
                            @click="parent.selectItem(item)"
                          >
                            close
                          </v-icon>
                        </v-chip>
                      </template>

                      <template #item="{item}">
                        <v-list-item-content>
                          <v-chip
                            dark
                            label
                            small
                          >
                            {{ convertMinutesToDisplayString(item, false, false) }}
                          </v-chip>
                        </v-list-item-content>
                      </template>
                    </v-combobox>
                  </v-col>

                  <v-col cols="12">
                    <v-expansion-panels accordion>
                      <!-- Delivery -->
                      <v-expansion-panel
                        dark
                        readonly
                      >
                        <v-expansion-panel-header
                          :hide-actions="false"
                          class="pa-0 purple lighten-1"
                        >
                          <div
                            class="v-subheader purple white--text"
                            style="width: 100%; height: 60px;"
                          >
                            <v-icon
                              class="pr-2"
                              dark
                            >
                              {{ $t('Settings.WorkingHour.Field.OrderingTimes.DeliveryTime.Icon') }}
                            </v-icon>
                            <span :class="{'caption': $vuetify.breakpoint.xsOnly}">
                              <lang-search-highlight
                                :lang-text="$t('Settings.WorkingHour.Field.OrderingTimes.DeliveryTime.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </div>

                          <v-spacer />

                          <template #actions>
                            <div class="pa-2">
                              <v-btn
                                @click="viewData.settings.delivery_time_range = viewData.settings.collection_time_range"
                              >
                                {{
                                  $t('Settings.WorkingHour.Field.OrderingTimes.DeliveryTime.CopyFromCollection').toLocaleUpperCase($i18n.locale)
                                }}
                              </v-btn>
                            </div>
                          </template>
                        </v-expansion-panel-header>

                        <v-container fluid>
                          <v-row>
                            <v-col cols="12">
                              <v-select
                                v-model="viewData.settings.delivery_time_range"
                                :items="viewData.settings.order_time_ranges.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.OrderingTimes.AvailableTimes')"
                                :readonly="!userCanManage"
                                color="purple lighten-1"
                                hide-details
                                item-text="title"
                                item-value="id"
                                multiple
                                small-chips
                                @change="onDeliveryTimeRangeChange"
                              />
                            </v-col>
                            <v-col cols="12">
                              <v-select
                                v-model="viewData.settings.delivery_time"
                                :items="viewData.settings.delivery_time_range.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.OrderingTimes.DeliveryTime.DefaultDeliveryTime')"
                                :readonly="(!viewData.settings.offer_delivery && !viewData.settings.offer_delivery_pos) || !userCanManage"
                                :rules="[(v) => !!v || $t('Settings.WorkingHour.Field.Error.Required')]"
                                color="purple lighten-1"
                                item-text="title"
                                item-value="id"
                                small-chips
                              />
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel>

                      <!-- Collection -->
                      <v-expansion-panel
                        dark
                        readonly
                      >
                        <v-expansion-panel-header
                          :hide-actions="false"
                          class="pa-0 blue darken-1"
                        >
                          <div
                            class="v-subheader blue darken-3 white--text"
                            style="width: 100%; height: 60px;"
                          >
                            <v-icon
                              class="pr-2"
                              dark
                            >
                              {{ $t('Settings.WorkingHour.Field.OrderingTimes.CollectionTime.Icon') }}
                            </v-icon>
                            <span :class="{'caption': $vuetify.breakpoint.xsOnly}">
                              <lang-search-highlight
                                :lang-text="$t('Settings.WorkingHour.Field.OrderingTimes.CollectionTime.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </div>

                          <v-spacer />

                          <template #actions>
                            <div class="pa-2">
                              <v-btn
                                @click="viewData.settings.collection_time_range = viewData.settings.delivery_time_range"
                              >
                                {{
                                  $t('Settings.WorkingHour.Field.OrderingTimes.CollectionTime.CopyFromCollection').toLocaleUpperCase($i18n.locale)
                                }}
                              </v-btn>
                            </div>
                          </template>
                        </v-expansion-panel-header>

                        <v-container fluid>
                          <v-row>
                            <v-col cols="12">
                              <v-select
                                v-model="viewData.settings.collection_time_range"
                                :items="viewData.settings.order_time_ranges.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.OrderingTimes.AvailableTimes')"
                                :readonly="!userCanManage"
                                color="purple lighten-1"
                                hide-details
                                item-text="title"
                                item-value="id"
                                multiple
                                small-chips
                                @change="onCollectionTimeRangeChange"
                              />
                            </v-col>
                            <v-col cols="12">
                              <v-select
                                v-model="viewData.settings.collection_time"
                                :items="viewData.settings.collection_time_range.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.OrderingTimes.CollectionTime.DefaultCollectionTime')"
                                :readonly="(!viewData.settings.offer_collection && !viewData.settings.offer_collection_pos) || !userCanManage"
                                :rules="[(v) => !!v || $t('Settings.WorkingHour.Field.Error.Required')]"
                                color="purple lighten-1"
                                item-text="title"
                                item-value="id"
                                small-chips
                              />
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel>

                      <!-- Self-Service -->
                      <v-expansion-panel
                        dark
                        readonly
                      >
                        <v-expansion-panel-header
                          :hide-actions="false"
                          class="pa-0 teal darken-1"
                        >
                          <div
                            class="v-subheader teal darken-3 white--text"
                            style="width: 100%; height: 60px;"
                          >
                            <v-icon
                              class="pr-2"
                              dark
                            >
                              {{ $t('Settings.WorkingHour.Field.OrderingTimes.SelfServiceTime.Icon') }}
                            </v-icon>
                            <span :class="{'caption': $vuetify.breakpoint.xsOnly}">
                              <lang-search-highlight
                                :lang-text="$t('Settings.WorkingHour.Field.OrderingTimes.SelfServiceTime.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </div>

                          <v-spacer />

                          <template #actions>
                            <div class="pa-2">
                              <v-btn
                                @click="viewData.settings.self_service_time_range = viewData.settings.collection_time_range"
                              >
                                {{
                                  $t('Settings.WorkingHour.Field.OrderingTimes.SelfServiceTime.CopyFromCollection').toLocaleUpperCase($i18n.locale)
                                }}
                              </v-btn>
                            </div>
                          </template>
                        </v-expansion-panel-header>

                        <v-container fluid>
                          <v-row>
                            <v-col cols="12">
                              <v-select
                                v-model="viewData.settings.self_service_time_range"
                                :items="viewData.settings.order_time_ranges.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.OrderingTimes.AvailableTimes')"
                                :readonly="!userCanManage"
                                color="purple lighten-1"
                                hide-details
                                item-text="title"
                                item-value="id"
                                multiple
                                small-chips
                                @change="onSelfServiceTimeRangeChange"
                              />
                            </v-col>
                            <v-col cols="12">
                              <v-select
                                v-model="viewData.settings.self_service_time"
                                :items="viewData.settings.self_service_time_range.map(t => {return {id: parseInt(t), title: convertMinutesToDisplayString(t, false, false)}})"
                                :label="$t('Settings.WorkingHour.Field.OrderingTimes.SelfServiceTime.DefaultCollectionTime')"
                                :readonly="(!viewData.settings.offer_self_service && !viewData.settings.offer_self_service_pos) || !userCanManage"
                                :rules="[(v) => !!v || $t('Settings.WorkingHour.Field.Error.Required')]"
                                color="purple lighten-1"
                                item-text="title"
                                item-value="id"
                                small-chips
                              />
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4 settings-window-actions">
      <v-spacer />

      <v-btn
        class="light-blue--text text--darken-1"
        outlined
        @click="onCancelButtonClick"
      >
        {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
      </v-btn>

      <v-btn
        v-if="userCanManage"
        :disabled="viewData===null || isSaving"
        :loading="isSaving"
        class="green white--text elevation-0"
        @click="onSaveButtonClick"
      >
        {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import OrderTypeTime from '@/mixins/orders/orderTypeTime'
import i18n from '@/lang/lang'
import LangSearchHighlightMixin from '@/lang/highlight/langSearchHighlightMixin.js'
import LangSearchHighlight from '@/lang/highlight/LangSearchHighlight.vue'

const defaultReservation = [
  {
    type              : 'reservation',
    weekday           : 0,
    weekday_name      : i18n.t('Common.Date.WeekDays.Long')[0],
    opening_time      : '13:00',
    closing_time      : '23:00',
    opening_time_after: null,
    closing_time_after: null,
    status            : 0
  },
  {
    type              : 'reservation',
    weekday           : 1,
    weekday_name      : i18n.t('Common.Date.WeekDays.Long')[1],
    opening_time      : '16:00',
    closing_time      : '00:30',
    opening_time_after: null,
    closing_time_after: null,
    status            : 1
  },
  {
    type              : 'reservation',
    weekday           : 2,
    weekday_name      : i18n.t('Common.Date.WeekDays.Long')[2],
    opening_time      : '16:00',
    closing_time      : '00:30',
    opening_time_after: null,
    closing_time_after: null,
    status            : 1
  },
  {
    type              : 'reservation',
    weekday           : 3,
    weekday_name      : i18n.t('Common.Date.WeekDays.Long')[3],
    opening_time      : '16:00',
    closing_time      : '23:00',
    opening_time_after: null,
    closing_time_after: null,
    status            : 1
  },
  {
    type              : 'reservation',
    weekday           : 4,
    weekday_name      : i18n.t('Common.Date.WeekDays.Long')[4],
    opening_time      : '16:00',
    closing_time      : '23:00',
    opening_time_after: null,
    closing_time_after: null,
    status            : 1
  },
  {
    type              : 'reservation',
    weekday           : 5,
    weekday_name      : i18n.t('Common.Date.WeekDays.Long')[5],
    opening_time      : '16:00',
    closing_time      : '23:00',
    opening_time_after: null,
    closing_time_after: null,
    status            : 1
  },
  {
    type              : 'reservation',
    weekday           : 6,
    weekday_name      : i18n.t('Common.Date.WeekDays.Long')[6],
    opening_time      : '12:00',
    closing_time      : '23:00',
    opening_time_after: null,
    closing_time_after: null,
    status            : 1
  }
]

export default {
  components: {LangSearchHighlight},
  mixins    : [AppData, Auth, OrderTypeTime, LangSearchHighlightMixin],
  data() {
    return {
      isSaving                : false,
      panel                   : null,
      tab                     : null,
      settingsFormValid       : false,
      opening_type            : 'same',
      delivery_type           : 'same',
      collection_type         : 'same',
      self_service_type       : 'same',
      reservation_type        : 'same',
      viewData                : null,
      workingHoursFormValid   : true,
      orderTimeRangesFormValid: true,
      restaurantOpen          : true,
      AddNewTimeSearch        : ''
    }
  },
  computed: {
    onlineOrderingStatus: {
      get() {
        return this.viewData.settings.online_ordering_active && (this.viewData.settings.offer_delivery || this.viewData.settings.offer_collection || this.viewData.settings.offer_self_service)
      },
      set(val) {
        this.viewData.settings.online_ordering_active = val
      }
    },
    openingReadonly() {
      return this.opening_type === '24'
    },
    deliveryReadonly() {
      return this.delivery_type === 'same' || (!this.viewData.settings.offer_delivery && !this.viewData.settings.offer_delivery_pos)
    },
    collectionReadonly() {
      return this.collection_type === 'same' || (!this.viewData.settings.offer_collection && !this.viewData.settings.offer_collection_pos)
    },
    selfServiceReadonly() {
      return this.self_service_type === 'same' || (!this.viewData.settings.offer_self_service && !this.viewData.settings.offer_self_service_pos)
    },
    reservationReadonly() {
      return this.reservation_type === 'same' || !this.viewData.settings.offer_reservation
    },
    isOpening247() {
      return this.viewData.opening?.filter(day => (day.opening_time_after === null && day.closing_time_after === null && day.opening_time === '00:00' && day.closing_time === '23:59') && day.status === 1)?.length === 7 ? '24' : '7'
    },
    isDeliverySameAsOpening() {
      const opening = this.viewData.opening
      return this.viewData.delivery?.filter((day, index) => (day.opening_time_after === opening[index].opening_time_after && day.closing_time_after === opening[index].closing_time_after && day.opening_time === opening[index].opening_time && day.closing_time === opening[index].closing_time) && day.status === opening[index].status)?.length === 7 ? 'same' : '7'
    },
    isCollectionSameAsOpening() {
      const opening = this.viewData.opening
      return this.viewData.collection?.filter((day, index) => (day.opening_time_after === opening[index].opening_time_after && day.closing_time_after === opening[index].closing_time_after && day.opening_time === opening[index].opening_time && day.closing_time === opening[index].closing_time) && day.status === opening[index].status)?.length === 7 ? 'same' : '7'
    },
    isSelfServiceSameAsOpening() {
      const opening = this.viewData.opening
      return this.viewData.self_service?.filter((day, index) => (day.opening_time_after === opening[index].opening_time_after && day.closing_time_after === opening[index].closing_time_after && day.opening_time === opening[index].opening_time && day.closing_time === opening[index].closing_time) && day.status === opening[index].status)?.length === 7 ? 'same' : '7'
    },
    isReservationSameAsOpening() {
      const opening = this.viewData.opening
      return this.viewData.reservation?.filter((day, index) => (day.opening_time_after === opening[index].opening_time_after && day.closing_time_after === opening[index].closing_time_after && day.opening_time === opening[index].opening_time && day.closing_time === opening[index].closing_time) && day.status === opening[index].status)?.length === 7 ? 'same' : '7'
    }
  },
  watch: {
    opening_type(val) {
      if (val && val === '24') {
        this.$nextTick(() => {
          this.panel = null
        })
        this.viewData.opening.forEach((day) => {
          day.opening_time = '00:00'
          day.closing_time = '23:59'
          day.opening_time_after = null
          day.closing_time_after = null
          day.status = 1
        })
      }
    },
    delivery_type(val) {
      if (val && val === 'same') {
        this.$nextTick(() => {
          this.panel = null
        })
        this.viewData.delivery.forEach((day, index) => {
          day.opening_time = this.viewData.opening[index].opening_time
          day.closing_time = this.viewData.opening[index].closing_time
          day.opening_time_after = this.viewData.opening[index].opening_time_after
          day.closing_time_after = this.viewData.opening[index].closing_time_after
          day.status = this.viewData.opening[index].status
        })
      }
    },
    collection_type(val) {
      if (val && val === 'same') {
        this.$nextTick(() => {
          this.panel = null
        })
        this.viewData.collection.forEach((day, index) => {
          day.opening_time = this.viewData.opening[index].opening_time
          day.closing_time = this.viewData.opening[index].closing_time
          day.opening_time_after = this.viewData.opening[index].opening_time_after
          day.closing_time_after = this.viewData.opening[index].closing_time_after
          day.status = this.viewData.opening[index].status
        })
      }
    },
    self_service_type(val) {
      if (val && val === 'same') {
        this.$nextTick(() => {
          this.panel = null
        })
        this.viewData.self_service.forEach((day, index) => {
          day.opening_time = this.viewData.opening[index].opening_time
          day.closing_time = this.viewData.opening[index].closing_time
          day.opening_time_after = this.viewData.opening[index].opening_time_after
          day.closing_time_after = this.viewData.opening[index].closing_time_after
          day.status = this.viewData.opening[index].status
        })
      }
    },
    reservation_type(val) {
      if (val && val === 'same') {
        this.$nextTick(() => {
          this.panel = null
        })
        this.viewData.reservation.forEach((day, index) => {
          day.opening_time = this.viewData.opening[index].opening_time
          day.closing_time = this.viewData.opening[index].closing_time
          day.opening_time_after = this.viewData.opening[index].opening_time_after
          day.closing_time_after = this.viewData.opening[index].closing_time_after
          day.status = this.viewData.opening[index].status
        })
      }
    }
  },
  created() {
    // console.log('1. created');
  },
  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.WorkingHours.All, this.setData)
    this.$bus.$on(window.SocketCommand.WorkingHours.Save, this.onSaveResult)
    this.getData()
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.WorkingHours.All, this.setData)
    this.$bus.$off(window.SocketCommand.WorkingHours.Save, this.onSaveResult)
  },
  methods: {
    onDeliveryTimeRangeChange(val) {
      if (!val.includes(this.viewData.settings.delivery_time)) this.viewData.settings.delivery_time = null
    },
    onCollectionTimeRangeChange(val) {
      if (!val.includes(this.viewData.settings.collection_time)) this.viewData.settings.collection_time = null
    },
    onSelfServiceTimeRangeChange(val) {
      if (!val.includes(this.viewData.settings.self_service_time)) this.viewData.settings.self_service_time = null
    },
    onOrderTimeRangeChange(val) {
      this.viewData.settings.delivery_time_range = this.viewData.settings.collection_time_range = val.map(t => parseInt(t))
      if (!this.viewData.settings.delivery_time_range.includes(this.viewData.settings.delivery_time)) this.viewData.settings.delivery_time = null
      if (!this.viewData.settings.collection_time_range.includes(this.viewData.settings.collection_time)) this.viewData.settings.collection_time = null
      if (!this.viewData.settings.self_service_time_range.includes(this.viewData.settings.self_service_time)) this.viewData.settings.self_service_time = null
    },
    getData() {
      window.callAS(window.SocketCommand.WorkingHours.All, {})
    },
    setData(data) {
      data.delivery && data.delivery.forEach((day, index) => {
        day.after_hours_toggle = !!((day.closing_time_after && day.opening_time_after))
      })

      data.collection && data.collection.forEach((day, index) => {
        day.after_hours_toggle = !!((day.closing_time_after && day.opening_time_after))
      })

      data.self_service && data.self_service.forEach((day, index) => {
        day.after_hours_toggle = !!((day.closing_time_after && day.opening_time_after))
      })

      if (!data.reservation) data.reservation = defaultReservation
      data.reservation && data.reservation.forEach((day, index) => {
        day.after_hours_toggle = !!((day.closing_time_after && day.opening_time_after))
      })

      data.opening && data.opening.forEach((day, index) => {
        day.after_hours_toggle = !!((day.closing_time_after && day.opening_time_after))
      })

      data.settings.delivery_time_range = data.settings.delivery_time_range?.map(t => parseInt(t)).sort((a, b) => a - b) || []
      data.settings.collection_time_range = data.settings.collection_time_range?.map(t => parseInt(t)).sort((a, b) => a - b) || []
      data.settings.self_service_time_range = data.settings.self_service_time_range?.map(t => parseInt(t)).sort((a, b) => a - b) || []
      data.settings.order_time_ranges = data.settings.order_time_ranges?.map(t => parseInt(t)).sort((a, b) => a - b) || []

      this.$set(this, 'viewData', data)
      this.collection_type = this.isCollectionSameAsOpening
      this.delivery_type = this.isDeliverySameAsOpening
      this.self_service_type = this.isSelfServiceSameAsOpening
      this.reservation_type = this.isReservationSameAsOpening
      this.opening_type = this.isOpening247
    },
    onCancelButtonClick() {
      this.$router.replace({name: 'SystemSettings'})
    },
    onSaveButtonClick() {
      this.viewData.opening.forEach((day, index) => {
        if (!day.after_hours_toggle) {
          day.opening_time_after = null
          day.closing_time_after = null
        }
      })

      if (this.viewData.delivery && this.delivery_type === 'same') {
        this.viewData.delivery.forEach((day, index) => {
          day.opening_time = this.viewData.opening[index].opening_time
          day.closing_time = this.viewData.opening[index].closing_time
          day.opening_time_after = this.viewData.opening[index].opening_time_after
          day.closing_time_after = this.viewData.opening[index].closing_time_after
          day.status = this.viewData.opening[index].status
          if (!day.after_hours_toggle) {
            day.opening_time_after = null
            day.closing_time_after = null
          }
        })
      }

      if (this.viewData.collection && this.collection_type === 'same') {
        this.viewData.collection.forEach((day, index) => {
          day.opening_time = this.viewData.opening[index].opening_time
          day.closing_time = this.viewData.opening[index].closing_time
          day.opening_time_after = this.viewData.opening[index].opening_time_after
          day.closing_time_after = this.viewData.opening[index].closing_time_after
          day.status = this.viewData.opening[index].status
          if (!day.after_hours_toggle) {
            day.opening_time_after = null
            day.closing_time_after = null
          }
        })
      }

      if (this.viewData.self_service && this.self_service_type === 'same') {
        this.viewData.self_service.forEach((day, index) => {
          day.opening_time = this.viewData.opening[index].opening_time
          day.closing_time = this.viewData.opening[index].closing_time
          day.opening_time_after = this.viewData.opening[index].opening_time_after
          day.closing_time_after = this.viewData.opening[index].closing_time_after
          day.status = this.viewData.opening[index].status
          if (!day.after_hours_toggle) {
            day.opening_time_after = null
            day.closing_time_after = null
          }
        })
      }

      if (this.viewData.reservation && this.reservation_type === 'same') {
        this.viewData.reservation.forEach((day, index) => {
          day.opening_time = this.viewData.opening[index].opening_time
          day.closing_time = this.viewData.opening[index].closing_time
          day.opening_time_after = this.viewData.opening[index].opening_time_after
          day.closing_time_after = this.viewData.opening[index].closing_time_after
          day.status = this.viewData.opening[index].status
          if (!day.after_hours_toggle) {
            day.opening_time_after = null
            day.closing_time_after = null
          }
        })
      }

      if (this.$refs.workingHoursForm) this.$refs.workingHoursForm.validate()
      if (this.$refs.orderTimeRangesForm) this.$refs.orderTimeRangesForm.validate()
      this.validateData()

      if (this.workingHoursFormValid && this.orderTimeRangesFormValid) {
        this.isSaving = true
        this.viewData.opening_type = this.isOpening247 === '7' ? 'flexible' : '24_7'
        this.viewData.delivery_type = this.isDeliverySameAsOpening === '7' ? '2' : '0'
        this.viewData.collection_type = this.isCollectionSameAsOpening === '7' ? '2' : '0'
        this.viewData.self_service_type = this.isSelfServiceSameAsOpening === '7' ? '2' : '0'
        this.viewData.reservation_type = this.isReservationSameAsOpening === '7' ? '2' : '0'
        window.callAS(window.SocketCommand.WorkingHours.Save, {working_hours: this.viewData})
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.FieldError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },
    onSaveResult(data) {
      this.isSaving = false
      if (data.status === 'success') {
        // this.$router.replace({ name: 'SystemSettings' })
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: data.type,
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.WorkingHour.Notification'),
          type: 'error',
          icon: 'warning'
        })
      }
    },
    allowedStartHours(value, openTimeObject, closeTimeObject) {
      return true
      /* const [endHour, endMinute] = closeTimeObject ? closeTimeObject.split(':') : '00:0'
                              return closeTimeObject ? value <= parseInt(endHour) : true */
    },
    allowedStartMinutes(value, openTimeObject, closeTimeObject) {
      return true
      /* const [startHour, startMinute] = openTimeObject ? openTimeObject.split(':') : '00:00'
                              const [endHour, endMinute]     = closeTimeObject ? closeTimeObject.split(':') : '00:00'
                              return openTimeObject && closeTimeObject ? parseInt(startHour) === parseInt(endHour) ? value > parseInt(endMinute) : true : true */
    },
    allowedEndHours(value, openTimeObject, closeTimeObject) {
      return true
      /* const [startHour, startMinute] = openTimeObject ? openTimeObject.split(':') : '00:00'
                              return openTimeObject ? value >= parseInt(startHour) : true */
    },
    allowedEndMinutes(value, openTimeObject, closeTimeObject) {
      return true
      /* const [startHour, startMinute] = openTimeObject ? openTimeObject.split(':') : '00:00'
                              const [endHour, endMinute]     = closeTimeObject ? closeTimeObject.split(':') : '00:00'
                              return openTimeObject && closeTimeObject ? parseInt(startHour) === parseInt(endHour) ? value > parseInt(startMinute) : true : true */
    },
    validateData() {
      let errorCount = 0
      let isValid = true
      this.viewData.opening.forEach((day) => {
        if (day.opening_time_after !== null || day.closing_time_after !== null) {
          if (day.opening_time_after !== null && day.closing_time_after !== null) {
            this.$set(day, 'error', false)
          } else {
            if (day.status) {
              this.$set(day, 'error', true)
              this.$set(day, 'error_messages', [this.$t('Settings.WorkingHour.Field.Error.RequiredBoth')])
              errorCount++
            } else {
              day.opening_time_after = null
              day.closing_time_after = null
            }
          }
        } else {
          this.$set(day, 'error', false)
          this.$set(day, 'error_messages', [])
        }
      })

      isValid = errorCount === 0
      if (this.workingHoursFormValid) {
        this.workingHoursFormValid = isValid
      } else {
        if (!isValid) this.workingHoursFormValid = isValid
      }

      return this.workingHoursFormValid
    },
    updateDeliveryTime(val) {
      if (val > 0 && parseInt(this.viewData.settings.delivery_time) === 120) return
      if (val < 0 && parseInt(this.viewData.settings.delivery_time) === 1) return
      this.viewData.settings.delivery_time = parseInt(this.viewData.settings.delivery_time) + val
    }
  }
}
</script>

<style scoped>
:deep(.v-input--selection-controls.v-input--switch) {
  display: inline-block;
  position: relative !important;
}

:deep(.v-expansion-panel__header) {
  padding: 0 !important;
}

:deep(.v-expansion-panel-content__wrap) {
  padding: 0;
}
</style>
